const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const GRAPH = 'https://graph.microsoft.com/v1.0';

let cachedSiteId = null;
const cachedListIds = {};
// In-flight lookups, shared: parallel callers on a cold start wait on one
// request instead of each firing their own. Cleared on failure so a retry
// asks again.
let siteIdPromise = null;
const listIdPromises = {};

async function resolveSiteId(token) {
  if (cachedSiteId) return cachedSiteId;
  if (!siteIdPromise) {
    siteIdPromise = fetchSiteId(token).catch((err) => { siteIdPromise = null; throw err; });
  }
  return siteIdPromise;
}

async function fetchSiteId(token) {
  const res = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to resolve SharePoint site (${res.status}): ${await res.text()}`);
  const site = await res.json();
  cachedSiteId = site.id;
  return cachedSiteId;
}

// ============================================================
// FIG LIST DEFINITIONS
// ============================================================

const LIST_DEFINITIONS = {
  FIG_Insureds: {
    displayName: 'FIG_Insureds',
    columns: [
      // Company Info
      { name: 'CompanyName', text: {} },
      { name: 'DBA', text: {} },
      { name: 'FEIN', text: {} },
      { name: 'SICCode', text: {} },
      { name: 'NAICSCode', text: {} },
      { name: 'NAICSDescription', text: {} },
      // Mailing Address
      { name: 'MailingAddress', text: {} },
      { name: 'MailingCity', text: {} },
      { name: 'MailingState', text: {} },
      { name: 'MailingZip', text: {} },
      // Billing Address
      { name: 'BillingAddress', text: {} },
      { name: 'BillingCity', text: {} },
      { name: 'BillingState', text: {} },
      { name: 'BillingZip', text: {} },
      // Primary Contact
      { name: 'PrimaryFirstName', text: {} },
      { name: 'PrimaryLastName', text: {} },
      { name: 'PrimaryTitle', text: {} },
      { name: 'PrimaryEmail', text: {} },
      { name: 'PrimaryPhone', text: {} },
      // Operations
      { name: 'OperationsDescription', text: { allowMultipleLines: true } },
      { name: 'AnnualRevenue', number: {} },
      { name: 'AnnualPayroll', number: {} },
      { name: 'SubcontractorCosts', number: {} },
      { name: 'EmployeeCount', number: {} },
      { name: 'NumberOfMembers', number: {} },
      // Status
      { name: 'Status', text: {} }, // Active / Inactive
      // Additional Company Info
      { name: 'EntityType', text: {} },
      { name: 'GLCode', text: {} },
      { name: 'WebsiteAddress', text: {} },
      { name: 'BusinessPhone', text: {} },
      { name: 'DateBusinessStarted', text: {} },
      { name: 'NatureOfBusiness', text: {} },
      { name: 'NCCIRiskId', text: {} },
      { name: 'CreditBureauName', text: {} },
      { name: 'RelatedGroup', text: {} },
      { name: 'Principal', text: {} },
      // Loss run authorization letter (2026-10-08). The insured's signed
      // authorization for the agency to pull loss runs. Edited only from the
      // Loss Runs tab block, which writes non-blank changed values; an unset
      // boolean stays absent (unknown), never false. First FIG_Insureds date
      // column — FIG_Insureds is in STRICT_DATE_LISTS so writes go through
      // toDateOnly.
      { name: 'LossRunAuthOnFile', boolean: {} },
      { name: 'LossRunAuthSignedDate', dateTime: { format: 'dateOnly' } },
      { name: 'LossRunAuthVaultPath', text: {} },
    ],
  },

  FIG_Buildings: {
    displayName: 'FIG_Buildings',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'Address', text: {} },
      { name: 'City', text: {} },
      { name: 'State', text: {} },
      { name: 'Zip', text: {} },
      { name: 'ConstructionType', text: {} },
      { name: 'ISOConstructionClass', text: {} },
      { name: 'YearBuilt', number: {} },
      { name: 'SquareFootage', number: {} },
      { name: 'Stories', number: {} },
      { name: 'OccupancyType', text: {} },
      { name: 'PPCRating', text: {} },
      { name: 'Sprinklered', boolean: {} },
      { name: 'FireAlarm', boolean: {} },
      { name: 'SecuritySystem', boolean: {} },
      { name: 'BuildingValue', number: {} },
      { name: 'BPPValue', number: {} },
      { name: 'BusinessIncomeValue', number: {} },
      // Roof & Construction Details
      { name: 'RoofType', text: {} },
      { name: 'TaxCode', text: {} },
      { name: 'BuildingCode', text: {} },
      { name: 'WindClass', text: {} },
      { name: 'HeatingSource', text: {} },
      // Improvements
      { name: 'ImprovementWiringYear', text: {} },
      { name: 'ImprovementPlumbingYear', text: {} },
      { name: 'ImprovementRoofingYear', text: {} },
      { name: 'ImprovementHeatingYear', text: {} },
      { name: 'ImprovementOtherYear', text: {} },
      { name: 'ImprovementOtherDesc', text: {} },
      // Burglar Alarm
      { name: 'BurglarAlarmType', text: {} },
      { name: 'BurglarAlarmCertNumber', text: {} },
      { name: 'BurglarAlarmExpDate', text: {} },
      { name: 'BurglarAlarmInstalledBy', text: {} },
      { name: 'BurglarAlarmExtent', text: {} },
      { name: 'BurglarAlarmGrade', text: {} },
      // Security
      { name: 'GuardsWatchmen', text: {} },
      { name: 'ClockHourly', text: {} },
      // Fire Protection
      { name: 'FireProtectionDesc', text: {} },
      { name: 'FireProtectionSprinklerPct', text: {} },
      { name: 'FireAlarmManufacturer', text: {} },
      { name: 'FireAlarmType', text: {} },
      { name: 'FireDistrictCode', text: {} },
      // Employees
      { name: 'FullTimeEmployees', number: {} },
      { name: 'PartTimeEmployees', number: {} },
      { name: 'InsideEmployees', number: {} },
      { name: 'OutsideEmployees', number: {} },
      // Occupancy
      { name: 'OwnerOrTenant', text: {} },
      { name: 'LeasedToOthers', boolean: {} },
      { name: 'LeasedArea', number: {} },
      { name: 'OpenToPublicArea', number: {} },
      { name: 'AnnualRevenues', number: {} },
      { name: 'HighestFloor', number: {} },
      // Exposures
      { name: 'RightExposure', text: {} },
      { name: 'LeftExposure', text: {} },
      { name: 'FrontExposure', text: {} },
      { name: 'RearExposure', text: {} },
      // Heating & Fire
      { name: 'PrimaryHeatType', text: {} },
      { name: 'SecondaryHeatType', text: {} },
      { name: 'DistanceToHydrant', text: {} },
      { name: 'FireStationDist', text: {} },
      { name: 'CodeNumber', text: {} },
      { name: 'ProtectionClass', text: {} },
      { name: 'Basements', number: {} },
      { name: 'BuildingDescription', text: {} },
      { name: 'CoverBuilding', boolean: {} },
      { name: 'CoverBPP', boolean: {} },
      { name: 'PerilsInsured', text: {} },
    ],
  },

  FIG_Officers: {
    displayName: 'FIG_Officers',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'OfficerName', text: {} },
      { name: 'OfficerTitle', text: {} },
      { name: 'OwnershipPct', number: {} },
      { name: 'Phone', text: {} },
      { name: 'Email', text: {} },
      { name: 'WCIncluded', boolean: {} },
      { name: 'DateOfBirth', text: {} },
      { name: 'Relationship', text: {} },
      { name: 'OfficerState', text: {} },
      { name: 'LocationNumber', text: {} },
      { name: 'Duties', text: {} },
      { name: 'WCClassCode', text: {} },
      { name: 'Remuneration', number: {} },
    ],
  },

  FIG_Vehicles: {
    displayName: 'FIG_Vehicles',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'VehicleYear', number: {} },
      { name: 'Make', text: {} },
      { name: 'Model', text: {} },
      { name: 'VIN', text: {} },
      { name: 'VehicleType', text: {} },
      { name: 'VehicleValue', number: {} },
      { name: 'UsageType', text: {} },
      { name: 'RadiusOfOperation', text: {} },
      { name: 'DriverAssignment', text: {} },
    ],
  },

  FIG_Policies: {
    displayName: 'FIG_Policies',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'LOB', text: {} },
      { name: 'Carrier', text: {} },
      { name: 'PolicyNumber', text: {} },
      { name: 'QuoteNumber', text: {} },
      { name: 'EffectiveDate', text: {} },
      { name: 'ExpirationDate', text: {} },
      { name: 'Premium', number: {} },
      { name: 'Status', text: {} },
      { name: 'Layer', text: {} },
      { name: 'AgencyBilled', boolean: {} },
      { name: 'BrokerContact', text: {} },
      { name: 'NAICCode', text: {} },
      { name: 'ProgramName', text: {} },
      { name: 'ProgramCode', text: {} },
      { name: 'Underwriter', text: {} },
      { name: 'UnderwriterOffice', text: {} },
      { name: 'ReferenceNumber', text: {} },
      { name: 'AdmittedStatus', text: {} },
      { name: 'StateTax', number: {} },
      { name: 'BrokerFee', number: {} },
      { name: 'PolicyFee', number: {} },
      { name: 'AgencyFee', number: {} },
      { name: 'AgencyFeePercent', number: {} },
      { name: 'CommissionPercent', number: {} },
      { name: 'CommissionAmount', number: {} },
      { name: 'StampingFee', number: {} },
      { name: 'InspectionFee', number: {} },
      { name: 'WholesaleBroker', text: {} },
      // Loss runs (2026-10-08). AgencyOfRecord = the RETAIL agency that wrote the
      // term (e.g. Marsh McLennan Agency, Texas First, Fragos Insurance Group) —
      // not the wholesaler, which stays in WholesaleBroker. LossRunSourceId =
      // FIG_LossRunSources item id as text; blank = unassigned.
      { name: 'AgencyOfRecord', text: {} },
      { name: 'LossRunSourceId', text: {} },
      { name: 'InvoiceFeesDisplay', text: {} },
      { name: 'CancellationEffectiveDate', text: {} },
      { name: 'CancellationReason', text: {} },
      { name: 'Deductible', number: {} },
      { name: 'ReplacementCost', boolean: {} },
      { name: 'Coinsurance', boolean: {} },
      { name: 'CoinsurancePercent', number: {} },
      { name: 'BusinessIncome', boolean: {} },
      { name: 'BusinessIncomeLimit', number: {} },
      { name: 'BusinessIncomeType', text: {} },
      { name: 'RentalValue', boolean: {} },
      { name: 'RentalValueLimit', number: {} },
      { name: 'BlanketCoverage', text: {} },
      { name: 'TerrorismCoverage', text: {} },
      { name: 'TerrorismExclusion', text: {} },
      { name: 'DomesticTerrorismExcluded', text: {} },
      { name: 'FungusCoverage', text: {} },
      { name: 'FungusLimit', number: {} },
      { name: 'FungusDeductible', number: {} },
      { name: 'FungusExclusion', text: {} },
      { name: 'AgreedValue', text: {} },
      { name: 'EquipmentBreakdown', text: {} },
      { name: 'EquipmentBreakdownLimit', number: {} },
      { name: 'EquipmentBreakdownDed', number: {} },
      { name: 'OrdinanceOrLawA', text: {} },
      { name: 'OrdinanceOrLawB', text: {} },
      { name: 'OrdinanceOrLawC', text: {} },
      { name: 'OrdinanceOrLawALimit', number: {} },
      { name: 'OrdinanceOrLawADed', number: {} },
      { name: 'OrdinanceOrLawBLimit', number: {} },
      { name: 'OrdinanceOrLawBDed', number: {} },
      { name: 'OrdinanceOrLawCLimit', number: {} },
      { name: 'OrdinanceOrLawCDed', number: {} },
      { name: 'EarthMovement', text: {} },
      { name: 'EarthMovementLimit', number: {} },
      { name: 'EarthMovementDed', number: {} },
      { name: 'EarthMovementDedDesc', text: {} },
      { name: 'Flood', text: {} },
      { name: 'FloodLimit', number: {} },
      { name: 'FloodDed', number: {} },
      { name: 'FloodDedDesc', text: {} },
      { name: 'WindHail', text: {} },
      { name: 'WindHailLimit', number: {} },
      { name: 'WindHailDed', number: {} },
      { name: 'WindHailDedDesc', text: {} },
      { name: 'NamedStorm', text: {} },
      { name: 'NamedStormLimit', number: {} },
      { name: 'NamedStormDed', number: {} },
      { name: 'NamedStormDedDesc', text: {} },
      { name: 'SubrogationWaiver', text: {} },
      // Auto-specific columns (ACORD 25 covered-autos checkboxes).
      // Expected values: 'Any Auto' | 'Owned Only' | 'Scheduled' |
      // 'Hired and Non-Owned' (or 'HNOA'), or a combination such as
      // 'Owned, Hired and Non-Owned'. Left blank, the ACORD 25 leaves every
      // covered-autos box unmarked rather than assuming ANY AUTO.
      { name: 'CoverageScope', text: {} },
      // GL-specific columns (ACORD 126)
      { name: 'CoverageForm', text: {} },
      { name: 'GeneralAggregateLimit', text: {} },
      { name: 'AggregateAppliesPer', text: {} },
      { name: 'ProductsCompOpsLimit', text: {} },
      { name: 'PersonalAdvInjuryLimit', text: {} },
      { name: 'EachOccurrenceLimit', text: {} },
      { name: 'FireDamageLimit', text: {} },
      { name: 'MedExpenseLimit', text: {} },
      { name: 'EmployeeBenefitsLimit', text: {} },
      { name: 'PDDeductible', text: {} },
      { name: 'BIDeductible', text: {} },
      { name: 'DeductibleBasis', text: {} },
      { name: 'PremOpsRate', text: {} },
      { name: 'ProductsRate', text: {} },
      { name: 'PremOpsPremium', text: {} },
      { name: 'ProductsPremium', text: {} },
      { name: 'WindHailDiffProvisions', boolean: {} },
      { name: 'NamedStormDiffProvisions', boolean: {} },
      { name: 'AdditionalNamedInsured', text: {} },
      // WC-specific columns (ACORD 130)
      { name: 'ELEachAccident', number: {} },
      { name: 'ELDiseasePolicy', number: {} },
      { name: 'ELDiseaseEachEmployee', number: {} },
      { name: 'WCTotalEstimatedPremium', number: {} },
      { name: 'WCTotalMinimumPremium', number: {} },
      { name: 'WCTotalDepositPremium', number: {} },
      { name: 'WCDeductibleAmount', number: {} },
    ],
  },

  // Loss runs tracker. Schema is OWNED by sharepoint-mcp/scripts/
  // provision_fig_lossrun_lists.js — `provision: false` keeps ensureList from
  // creating these lists and ensureMissingColumns from adding columns to them.
  // These definitions must match that script exactly; a mismatch is logged as
  // a warning on first read and nothing is created.
  FIG_LossRunSources: {
    displayName: 'FIG_LossRunSources',
    provision: false,
    columns: [
      { name: 'Kind', choice: { choices: ['Carrier', 'Wholesaler', 'MGA', 'Prior Agency', 'Insured Direct'], allowTextEntry: false } },
      { name: 'ContactName', text: {} },
      { name: 'ContactEmail', text: {} },
      { name: 'ContactPhone', text: {} },
      { name: 'RequiresInsuredAuthorization', boolean: {} },
      { name: 'DoNotContact', boolean: {} },
      { name: 'DefaultMethod', choice: { choices: ['Loss Run', 'No Known Loss Letter', 'Reuse On File', 'Skip'], allowTextEntry: false } },
      { name: 'PersonId', text: {} },
      { name: 'Notes', text: { allowMultipleLines: true, textType: 'plain' } },
    ],
  },

  FIG_LossRunRequests: {
    displayName: 'FIG_LossRunRequests',
    provision: false,
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'LossRunSourceId', text: {} },
      { name: 'TermsCovered', text: {} },
      { name: 'Method', choice: { choices: ['Loss Run', 'No Known Loss Letter'], allowTextEntry: false } },
      { name: 'DateRequested', dateTime: { format: 'dateOnly' } },
      { name: 'DateReceived', dateTime: { format: 'dateOnly' } },
      { name: 'ValuedAsOf', dateTime: { format: 'dateOnly' } },
      { name: 'VaultPath', text: {} },
      { name: 'MinutesSpent', number: {} },
      { name: 'Notes', text: { allowMultipleLines: true, textType: 'plain' } },
      // Comma-separated FIG_Policies item ids this entry covers; blank = every
      // eligible policy of the source. Added to the provisioner 2026-10-08
      // (--add-missing); until it is applied live the app logs a warning and
      // treats every entry as covering the whole source.
      { name: 'PolicyIds', text: { allowMultipleLines: true, textType: 'plain' } },
    ],
  },

  // "Handled, and why" per renewal season. Compliance record: the app only
  // ever adds rows (a correction is a newer row). Schema owned by the same
  // provisioner; provision: false. PolicyIds is required by the app's form
  // validation, not by SharePoint.
  FIG_LossRunResolutions: {
    displayName: 'FIG_LossRunResolutions',
    provision: false,
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'RenewalYear', text: {} },
      { name: 'PolicyIds', text: { allowMultipleLines: true, textType: 'plain' } },
      { name: 'Status', choice: { choices: ['Satisfied', 'Not required', 'Still open'], allowTextEntry: false } },
      { name: 'Reason', choice: { choices: ['Loss run provided', 'No known loss letter accepted', 'Underwriter did not require', 'Same carrier renewing', 'Still chasing', 'Other'], allowTextEntry: false } },
      { name: 'DecidedBy', text: {} },
      { name: 'DecidedDate', dateTime: { format: 'dateOnly' } },
      { name: 'Evidence', text: {} },
      { name: 'Notes', text: { allowMultipleLines: true, textType: 'plain' } },
    ],
  },

  FIG_Agency: {
    displayName: 'FIG_Agency',
    columns: [
      { name: 'AgencyName', text: {} },
      { name: 'AgencyAddress', text: {} },
      { name: 'AgencyCity', text: {} },
      { name: 'AgencyState', text: {} },
      { name: 'AgencyZip', text: {} },
      { name: 'ContactName', text: {} },
      { name: 'ContactPhone', text: {} },
      { name: 'ContactFax', text: {} },
      { name: 'ContactEmail', text: {} },
      { name: 'ProducerCode', text: {} },
      { name: 'SubCode', text: {} },
      { name: 'AgencyCustomerId', text: {} },
      { name: 'NationalProducerNumber', text: {} },
      { name: 'StateLicenseNumber', text: {} },
      { name: 'ProducerName', text: {} },
    ],
  },

  FIG_Contacts: {
    displayName: 'FIG_Contacts',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'ContactName', text: {} },
      { name: 'ContactType', text: {} },
      { name: 'Phone', text: {} },
      { name: 'Email', text: {} },
      { name: 'SecondaryEmail', text: {} },
    ],
  },

  FIG_PolicyFees: {
    displayName: 'FIG_PolicyFees',
    columns: [
      { name: 'PolicyId', number: {} },
      { name: 'FeeLabel', text: {} },
      { name: 'Amount', number: {} },
      { name: 'Notes', text: {} },
    ],
  },

  FIG_CarrierHistory: {
    displayName: 'FIG_CarrierHistory',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'LOB', text: {} },
      { name: 'PolicyYear', text: {} },
      { name: 'Carrier', text: {} },
      { name: 'PolicyNumber', text: {} },
      { name: 'Premium', number: {} },
      { name: 'AgentOfRecord', text: {} },
      { name: 'LossRunContact', text: {} },
      { name: 'LossRunEmail', text: {} },
      { name: 'LossRunPhone', text: {} },
      { name: 'ClaimsSummary', text: {} },
      { name: 'EffectiveDate', text: {} },
      { name: 'ExpirationDate', text: {} },
    ],
  },

  FIG_LossHistory: {
    displayName: 'FIG_LossHistory',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'LOB', text: {} },
      { name: 'DateOfOccurrence', text: {} },
      { name: 'DateOfClaim', text: {} },
      { name: 'Description', text: {} },
      { name: 'AmountPaid', number: {} },
      { name: 'AmountReserved', number: {} },
      { name: 'ClaimOpen', boolean: {} },
      { name: 'Subrogation', boolean: {} },
    ],
  },

  FIG_ClassCodes: {
    displayName: 'FIG_ClassCodes',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'CodeType', text: {} },
      { name: 'Code', text: {} },
      { name: 'Description', text: {} },
      { name: 'LocationNumber', text: {} },
      { name: 'OperationType', text: {} },
      { name: 'PremiumBasis', text: {} },
      { name: 'Exposure', text: {} },
      { name: 'Territory', text: {} },
      { name: 'Rate', number: {} },
      { name: 'Premium', number: {} },
    ],
  },

  FIG_Coverages: {
    displayName: 'FIG_Coverages',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'PolicyId', text: {} },
      { name: 'LOB', text: {} },
      { name: 'CoverageType', text: {} },
      { name: 'Limit', number: {} },
      { name: 'Deductible', number: {} },
      { name: 'DeductibleType', text: {} },
      { name: 'Premium', number: {} },
      { name: 'CoinsurancePct', number: {} },
      { name: 'ValuationType', text: {} },
      { name: 'CausesOfLoss', text: {} },
      { name: 'InflationGuardPct', number: {} },
      { name: 'BlanketNumber', text: {} },
      { name: 'FormsAndConditions', text: {} },
      { name: 'CoveredAutoSymbols', text: {} },
      { name: 'SubjectOfInsurance', text: {} },
      { name: 'PerilsInsured', text: {} },
      { name: 'BusinessIncome', boolean: {} },
      { name: 'BusinessIncomeLimit', number: {} },
      { name: 'BusinessIncomeType', text: {} },
      { name: 'BlanketCoverage', boolean: {} },
      { name: 'ReplacementCost', boolean: {} },
      { name: 'AgreedValue', boolean: {} },
      { name: 'TerrorismCoverage', boolean: {} },
      { name: 'TerrorismExclusion', boolean: {} },
      { name: 'DomesticTerrorismExcluded', boolean: {} },
      { name: 'FungusCoverage', boolean: {} },
      { name: 'EarthMovement', boolean: {} },
      { name: 'EarthMovementLimit', number: {} },
      { name: 'EarthMovementDeductible', text: {} },
      { name: 'FloodCoverage', boolean: {} },
      { name: 'FloodLimit', number: {} },
      { name: 'FloodDeductible', text: {} },
      { name: 'WindHail', boolean: {} },
      { name: 'WindHailLimit', number: {} },
      { name: 'WindHailDeductible', text: {} },
      { name: 'NamedStorm', boolean: {} },
      { name: 'NamedStormLimit', number: {} },
      { name: 'NamedStormDeductible', text: {} },
      { name: 'EquipmentBreakdown', boolean: {} },
      { name: 'OrdinanceOrLaw', boolean: {} },
      { name: 'PermissionToWaiveSubrogation', boolean: {} },
      { name: 'RentalValue', boolean: {} },
      { name: 'RentalValueLimit', number: {} },
    ],
  },

  FIG_GeneralQuestions: {
    displayName: 'FIG_GeneralQuestions',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'FormType', text: {} },
      { name: 'QuestionNumber', text: {} },
      { name: 'QuestionText', text: {} },
      { name: 'Answer', text: {} },
      { name: 'Explanation', text: {} },
    ],
  },

  FIG_WCRating: {
    displayName: 'FIG_WCRating',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'RatingState', text: {} },
      { name: 'LocationNumber', text: {} },
      { name: 'ClassCode', text: {} },
      { name: 'ClassDescription', text: {} },
      { name: 'SIC', text: {} },
      { name: 'NAICS', text: {} },
      { name: 'FullTimeEmployees', number: {} },
      { name: 'PartTimeEmployees', number: {} },
      { name: 'EstimatedPayroll', number: {} },
      { name: 'Rate', number: {} },
      { name: 'ManualPremium', number: {} },
      { name: 'ExperienceMod', number: {} },
      { name: 'StandardPremium', number: {} },
      { name: 'ScheduleRating', number: {} },
      { name: 'PremiumDiscount', number: {} },
      { name: 'ExpenseConstant', number: {} },
      { name: 'TotalEstimatedPremium', number: {} },
      { name: 'MinimumPremium', number: {} },
      { name: 'DepositPremium', number: {} },
    ],
  },

  FIG_AdditionalInterests: {
    displayName: 'FIG_AdditionalInterests',
    columns: [
      { name: 'InsuredId', text: {} },
      { name: 'PolicyId', text: {} },
      { name: 'InterestType', text: {} },
      { name: 'EntityName', text: {} },
      { name: 'Address', text: {} },
      { name: 'City', text: {} },
      { name: 'State', text: {} },
      { name: 'Zip', text: {} },
      { name: 'Phone', text: {} },
      { name: 'Email', text: {} },
      { name: 'ReferenceNumber', text: {} },
      { name: 'Rank', text: {} },
      { name: 'LocationNumber', text: {} },
      { name: 'ItemDescription', text: {} },
      { name: 'EvidenceRequired', boolean: {} },
      { name: 'Notes', text: {} },
      { name: 'EndorsementId', text: {} },
      { name: 'ContractRequirements', text: {} },
      { name: 'LenderServicingAgent', text: {} },
      { name: 'LenderServicingAgentAddress', text: {} },
      { name: 'PropertyType', text: {} },
      // Multi-select. Which ACORD 28 designation boxes this interest carries.
      // InterestType is kept alongside it and still distinguishes property
      // interests from Other Named Insured records.
      {
        name: 'Designations',
        choice: {
          allowTextEntry: false,
          displayAs: 'checkBoxes',
          choices: ['Mortgagee', "Lender's Loss Payable", 'Loss Payee', 'Contract of Sale'],
        },
      },
    ],
  },

  FIG_LayerParticipants: {
    displayName: 'FIG_LayerParticipants',
    columns: [
      { name: 'PolicyId', text: {} },
      { name: 'InsuredId', text: {} },
      { name: 'ParticipantName', text: {} },
      { name: 'ParticipantType', text: {} },
      { name: 'Percentage', number: {} },
      { name: 'ReferenceNumber', text: {} },
      { name: 'PolicyNumber', text: {} },
      { name: 'Status', text: {} },
    ],
  },

  FIG_Endorsements: {
    displayName: 'FIG_Endorsements',
    columns: [
      { name: 'PolicyId', text: {} },
      { name: 'InsuredId', text: {} },
      { name: 'EndorsementNumber', text: {} },
      { name: 'EffectiveDate', text: {} },
      { name: 'Description', text: {} },
      { name: 'EndorsementType', text: {} },
      { name: 'PremiumChange', number: {} },
      { name: 'Status', text: {} },
      { name: 'RequestDate', text: {} },
      { name: 'IssuedDate', text: {} },
      { name: 'Notes', text: {} },
    ],
  },

  FIG_Carriers: {
    displayName: 'FIG_Carriers',
    columns: [
      { name: 'NAICCode', text: {} },
      { name: 'NAICGroup', text: {} },
      { name: 'CompanyName', text: {} },
      { name: 'EIN', text: {} },
      { name: 'DomicileState', text: {} },
      { name: 'Address', text: {} },
      { name: 'City', text: {} },
      { name: 'State', text: {} },
      { name: 'Zip', text: {} },
      { name: 'Phone', text: {} },
      { name: 'CompanyType', text: {} },
      { name: 'LicensedLines', text: {} },
      { name: 'CarrierType', text: {} },
      { name: 'IsMyCarrier', boolean: {} },
    ],
  },

  FIG_LinesOfBusiness: {
    displayName: 'FIG_LinesOfBusiness',
    columns: [
      { name: 'Abbreviation', text: {} },
    ],
  },

  FamilyVault_Media_Items: {
    displayName: 'FamilyVault_Media_Items',
    columns: [
      // Universal
      { name: 'MediaType', choice: { choices: ['Audiobook', 'Music', 'Movie', 'Video', 'Photo'], displayAs: 'dropDownMenu' } },
      { name: 'Notes', text: { allowMultipleLines: true } },
      { name: 'Tags', text: { allowMultipleLines: true } },
      { name: 'Favorite', boolean: {} },
      { name: 'StorageLocation', text: {} },
      { name: 'FileFormat', text: {} },
      { name: 'Source', text: {} },
      { name: 'CoverImageUrl', text: {} },
      // Movie
      { name: 'ReleaseYear', number: {} },
      { name: 'SharedResumePosition', number: {} },
      { name: 'Completed', boolean: {} },
      // Audiobook
      { name: 'BookAuthor', text: {} },
      { name: 'Narrator', text: {} },
      { name: 'SeriesName', text: {} },
      { name: 'BookNumber', number: {} },
      { name: 'BookTitle', text: {} },
      { name: 'ChapterNumber', number: {} },
      { name: 'Duration', number: {} },
      // Music
      { name: 'Artist', text: {} },
      { name: 'Album', text: {} },
      { name: 'TrackNumber', number: {} },
      { name: 'Composer', text: {} },
      { name: 'Work', text: {} },
      { name: 'Movement', text: {} },
      { name: 'Conductor', text: {} },
      { name: 'Performer', text: {} },
      { name: 'RecordingYear', number: {} },
      // Video / Photo
      { name: 'DateOfEvent', dateTime: { format: 'dateOnly' } },
      { name: 'People', text: { allowMultipleLines: true } },
      { name: 'Location', text: {} },
      { name: 'AlbumCollection', text: {} },
    ],
  },

  FamilyVault_Bookmarks: {
    displayName: 'FamilyVault_Bookmarks',
    columns: [
      { name: 'User', choice: { choices: ['Mommy', 'Daddy', 'Levi', 'Taylor', 'John'], displayAs: 'dropDownMenu' } },
      { name: 'MediaItemId', number: {} },
      { name: 'Position', number: {} },
      { name: 'Completed', boolean: {} },
    ],
  },

  FamilyVault_UserProgress: {
    displayName: 'FamilyVault_UserProgress',
    columns: [
      { name: 'UserEmail',    text: {} },
      { name: 'MediaItemId',  number: {} },
      { name: 'Position',     number: {} },
      { name: 'LastPlayedAt', text: {} },   // ISO 8601 — "most recent chapter" pointer for book-level resume
    ],
  },

  FamilyVault_NamedBookmarks: {
    displayName: 'FamilyVault_NamedBookmarks',
    columns: [
      { name: 'UserEmail',   text: {} },
      { name: 'MediaItemId', number: {} },   // the chapter/track the bookmark is in
      { name: 'Label',       text: {} },     // auto "Bookmark H:MM:SS"; user-renamable
      { name: 'Position',    number: {} },   // seconds
    ],
  },

  FIG_Invoices: {
    displayName: 'FIG_Invoices',
    columns: [
      { name: 'InvoiceDate', dateTime: {} },
      { name: 'ClientId', number: {} },
      { name: 'ClientName', text: {} },
      { name: 'Description', text: {} },
      { name: 'Job', text: {} },
      { name: 'ReferenceNumber', text: {} },
      { name: 'GrandTotal', number: {} },
      { name: 'PoliciesIncluded', text: { allowMultipleLines: true } },
      { name: 'Status', text: {} },
      { name: 'GeneratedDate', dateTime: {} },
      { name: 'DisplayMode', text: {} },
    ],
  },

  FIG_Settings: {
    displayName: 'FIG_Settings',
    columns: [
      { name: 'AgencyLegalName', text: {} },
      { name: 'AgencyAddress', text: {} },
      { name: 'AgencyCity', text: {} },
      { name: 'AgencyState', text: {} },
      { name: 'AgencyZip', text: {} },
      { name: 'AgencyPhone', text: {} },
      { name: 'AgencyEmail', text: {} },
      { name: 'AgencyWebsite', text: {} },
      { name: 'BrandPrimaryHex', text: {} },
      { name: 'BrandSecondaryHex', text: {} },
      { name: 'BrandTertiaryHex', text: {} },
      { name: 'BrandAccentHex', text: {} },
      { name: 'BrandTextHex', text: {} },
      { name: 'BrandLogoHorizontalPath', text: {} },
      { name: 'BrandLeafPath', text: {} },
      { name: 'PaymentBankName', text: {} },
      { name: 'PaymentRouting', text: {} },
      { name: 'PaymentAccount', text: {} },
      { name: 'PaymentPayableTo', text: {} },
    ],
  },

  TriageQueue: {
    displayName: 'TriageQueue',
    columns: [
      { name: 'Source', text: {} },
      { name: 'SenderEmail', text: {} },
      { name: 'SenderName', text: {} },
      { name: 'EmailSubject', text: {} },
      { name: 'ReceivedDate', text: {} },
      { name: 'FileUrl', text: {} },
      { name: 'StagingPath', text: {} },
      { name: 'FileSize', number: {} },
      { name: 'FileMimeType', text: {} },
      { name: 'EmailBody', text: { allowMultipleLines: true } },
      { name: 'EmailMessageId', text: {} },
      { name: 'Status', text: {} },
      { name: 'FiledTo', text: {} },
      { name: 'FiledBy', text: {} },
      { name: 'FiledDate', text: {} },
      { name: 'Side', text: {} },
      { name: 'EntityName', text: {} },
      { name: 'EntityType', text: {} },
      { name: 'DocType', text: {} },
      { name: 'PolicyDocType', text: {} },
      { name: 'LOB', text: {} },
      { name: 'EffectiveDate', text: {} },
      { name: 'ExpirationDate', text: {} },
      { name: 'Layer', text: {} },
      { name: 'PolicyId', text: {} },
      { name: 'OriginalSenderName', text: {} },
      { name: 'OriginalSenderEmail', text: {} },
    ],
  },

  FIG_NoteLinks: {
    displayName: 'FIG_NoteLinks',
    columns: [
      { name: 'SourceItemType',   text: {} },
      { name: 'SourceItemId',     text: {} },
      { name: 'OneNotePageId',    text: {} },
      { name: 'OneNoteClientUrl', text: { allowMultipleLines: true } },
      { name: 'OneNoteWebUrl',    text: { allowMultipleLines: true } },
      { name: 'UserUpn',          text: {} },
      { name: 'CreatedAt',        dateTime: {} },
    ],
  },

  // Food_Log — provisioned 2026-08-02 by sharepoint-mcp/scripts/provision_food_log_list.js,
  // list GUID 70d3feea-bc76-48cb-9e0c-273e579b2e8e. Date is INDEXED on the server; that is
  // not expressible here, and figApi never re-reads or sets it. Macros and minerals are
  // snapshot columns — denormalized at log time so a catalog correction cannot rewrite
  // history. decimalPlaces is omitted per this file's universal convention (0 of 121 number
  // columns set it). displayAs is omitted on both choice columns: they are single-select,
  // and the checkBoxes test in updateItem keys the Collection(Edm.String) annotation
  // off 'checkBoxes'.
  Food_Log: {
    displayName: 'Food_Log',
    columns: [
      { name: 'Date',        dateTime: { format: 'dateOnly' } },
      // Added to the live list after provisioning, by
      // sharepoint-mcp/scripts/add_food_log_time_column.js. It must be declared
      // here too: the createItem/updateItem allowlist is built from these
      // columns, so an undeclared Time is dropped from every payload silently —
      // no throw, no 400, just a row that comes back without the time it was
      // written with. See the Time guard below for why it is text.
      { name: 'Time',        text:     {} },
      // MealSlot is retained deliberately. The Today view no longer reads it —
      // the log is one chronological ledger keyed on Time — but the column holds
      // data on existing rows and dropping it from this definition would delete
      // it from the allowlist, not from SharePoint.
      { name: 'MealSlot',    choice:   { choices: ['Breakfast', 'Lunch', 'Dinner', 'Snack'] } },
      { name: 'FoodId',      text:     {} },
      { name: 'Quantity',    number:   {} },
      { name: 'Unit',        text:     {} },
      { name: 'Grams',       number:   {} },
      { name: 'Calories',    number:   {} },
      { name: 'Protein',     number:   {} },
      { name: 'Fat',         number:   {} },
      { name: 'Carbs',       number:   {} },
      { name: 'Sodium',      number:   {} },
      { name: 'Potassium',   number:   {} },
      { name: 'Magnesium',   number:   {} },
      // UNIT NAMING RULE for this list: a bare nutrient name means MILLIGRAMS.
      // A unit suffix means that unit.
      //
      // Sodium, Potassium, Magnesium and Calcium are bare and are all mg. The
      // first three already hold live rows and cannot be renamed without
      // rewriting them, so rather than suffix everything, new columns in any
      // other unit carry the unit in the name instead.
      //
      // This matters because a row queried straight from SharePoint is column
      // names and numbers with nothing else around it. 'Iodine: 90' is
      // unreadable — 90mg of iodine is a toxic dose, 90mcg is most of a day's
      // requirement, and the difference is 1000x with nothing on screen to say
      // which was meant. 'IodineMcg: 90' cannot be misread.
      { name: 'IodineMcg',   number:   {} },
      { name: 'Calcium',     number:   {} },
      { name: 'RetinolMcg',  number:   {} },
      { name: 'EntrySource', choice:   { choices: ['Catalog', 'Estimated', 'Manual'] } },
      { name: 'Notes',       text:     { allowMultipleLines: true } },
    ],
  },

  // Watch module — provisioned by sharepoint-mcp/scripts/provision_watch_lists.js.
  // Status (Watch_Open), WatchId and ConversationId (Watch_Sources) are INDEXED
  // on the server; that is not expressible here, same as Food_Log.Date. Nothing
  // carries a defaultValue and nothing beyond the built-in Title is required:
  // Triaged absent vs false is a real distinction (never triaged vs parked back)
  // and it drives the NEW rail — writers must OMIT the key, never send false for
  // "not known". Both lists are in STRICT_DATE_LISTS, so every dateOnly column
  // goes through the toDateOnly/fromDateOnly guard pair.
  Watch_Open: {
    displayName: 'Watch_Open',
    columns: [
      { name: 'WatchId',      number:   {} },
      { name: 'Kind',         choice:   { choices: ['Action', 'Waiting', 'Decision', 'Clock', 'Question', 'Anchor'] } },
      { name: 'Domain',       choice:   { choices: ['FIG', 'Family', 'Body', 'Platform'] } },
      { name: 'Status',       choice:   { choices: ['Open', 'Closed', 'Killed'] } }, // INDEXED on the server
      { name: 'Triaged',      boolean:  {} },
      { name: 'Pinned',       boolean:  {} },   // D3: holds a NOW slot on the board
      { name: 'Court',        choice:   { choices: ['Mine', 'Theirs', 'Holding'] } },
      { name: 'Counterparty', text:     {} },
      { name: 'LastTouched',  dateTime: { format: 'dateOnly' } },
      { name: 'ColdDays',     number:   {} },
      { name: 'NextAction',   text:     { allowMultipleLines: true } },
      { name: 'DueDate',      dateTime: { format: 'dateOnly' } },
      { name: 'WakeDate',     dateTime: { format: 'dateOnly' } },
      { name: 'WakeAfter',    number:   {} },
      { name: 'InsuredName',  text:     {} },
      { name: 'Person',       text:     {} },
      { name: 'AnchorId',     number:   {} },
      { name: 'Confidence',   text:     {} },
      { name: 'Project',      text:     {} },
      // Pipe-delimited, deliberately not semicolon — SharePoint uses ";#"
      // internally in several field types. CONSTRAINT: no tag may contain '|'
      // (verified clean across the 38-tag seed vocabulary, 2026-08-26).
      { name: 'Tags',         text:     {} },
      { name: 'LinksJson',    text:     { allowMultipleLines: true } },
      { name: 'Notes',        text:     { allowMultipleLines: true } },
      { name: 'Checklist',    text:     { allowMultipleLines: true } },
      { name: 'HeatOverride', number:   {} },
      { name: 'ParkCount',    number:   {} },
      { name: 'NowSlot',      number:   {} },
      { name: 'ArchivedAt',   dateTime: { format: 'dateOnly' } },
      // Server-owned columns (provision_watch_lists.js), declared here so the
      // allowlist in createItem/updateItem stops silently dropping them. The
      // browser READS them; it writes none of them today except through the
      // Projects layer below. CounterpartyEmail is the sweep's routing key;
      // the six Synopsis* columns are the sweep's status block (D1.5).
      { name: 'CounterpartyEmail',  text:     {} },
      { name: 'Synopsis',           text:     { allowMultipleLines: true } },
      { name: 'SynopsisCourt',      text:     {} },
      { name: 'SynopsisNextDate',   dateTime: { format: 'dateOnly' } },
      { name: 'SynopsisConfidence', number:   {} },
      { name: 'SynopsisAt',         dateTime: {} },
      { name: 'SynopsisHash',       text:     {} },
      // Projects layer (2026-09-15): ProjectId is a Watch_Projects.ProjectId
      // string, not a lookup. The lock flags follow the Triaged norm — absent
      // means "never placed by hand"; writers set true, never send false for
      // "not known".
      { name: 'ProjectId',          text:     {} },
      { name: 'ProjectLocked',      boolean:  {} },
      { name: 'TitleLocked',        boolean:  {} },
    ],
  },

  // Child list, not a blob: ConversationId must be indexed and queryable, and a
  // blob cannot be indexed. Also ends one-source-copied-into-five-records.
  // Relationship is WatchId-to-WatchId by indexed number match, NOT a SharePoint
  // lookup column — figApi has no lookup handling and none is wanted.
  Watch_Sources: {
    displayName: 'Watch_Sources',
    columns: [
      { name: 'WatchId',           number:   {} }, // parent Watch_Open.WatchId; INDEXED on the server
      { name: 'SourceKind',        choice:   { choices: ['email', 'call', 'meeting', 'portal', 'note', 'doc'] } },
      { name: 'SourceDate',        dateTime: { format: 'dateOnly' } },
      { name: 'Who',               text:     {} },
      { name: 'Org',               text:     {} },
      { name: 'RequestedBy',       text:     {} },
      { name: 'RequestedByOrg',    text:     {} },
      { name: 'Subject',           text:     {} },
      { name: 'Why',               text:     { allowMultipleLines: true } },
      { name: 'Mailbox',           text:     {} },
      { name: 'ConversationId',    text:     {} }, // INDEXED on the server
      { name: 'InternetMessageId', text:     {} },
      { name: 'WebLink',           text:     {} }, // longest seed value is 232 chars; fits single-line 255
      { name: 'IsReplyTarget',     boolean:  {} },
      { name: 'IsReAsk',           boolean:  {} },
      // Server-provisioned (provision_watch_lists.js), declared so the browser's
      // attachSources / mergeIntoCard writes of them land instead of being
      // stripped by the allowlist. MessageId is the Graph message id behind the
      // mailbox-scoped OWA deep link; the rest are the D1 intake columns.
      { name: 'MessageId',         text:     {} },
      { name: 'Ask',               text:     { allowMultipleLines: true } },
      { name: 'Deadline',          dateTime: { format: 'dateOnly' } },
      { name: 'Lane',              text:     {} },
      { name: 'Summary',           text:     {} },
      { name: 'ExtractConfidence', number:   {} },
      { name: 'AttachSuggested',   number:   {} },
      { name: 'Direction',         text:     {} },
      // Provenance — which tier filed the row (thread | policy | email | domain
      // | owner | new | catchup-* on the server; attach | merge from here).
      // Declared so the allowlist lets the app's stamps through.
      { name: 'RoutedBy',          text:     {} },
    ],
  },

  // The Projects layer — one row per project, a three-level tree by
  // ParentProjectId (a ProjectId string, blank at the top). Provisioned by
  // sharepoint-mcp/scripts/provision_watch_lists.js, which also indexes and
  // unique-constrains ProjectId (not expressible here). Title = Name.
  // My_List — Marc's List, the owner task list that rides on Watch cards.
  // Provisioned by the server's my_list tool (TaskId indexed + unique there,
  // not expressible here). The app READS it with getItems; every write goes
  // through the /api/my-list relay so the card side (Notes line, note source
  // row, synopsis re-read) happens with it. DoneAt null = not done.
  My_List: {
    displayName: 'My_List',
    columns: [
      { name: 'TaskId',    text:     {} },
      { name: 'Owner',     text:     {} },
      { name: 'Status',    choice:   { choices: ['Open', 'Done', 'Dropped'] } },
      { name: 'Note',      text:     { allowMultipleLines: true } },
      { name: 'WatchId',   number:   {} },
      { name: 'ProjectId', text:     {} },
      { name: 'DoneAt',    dateTime: { format: 'dateOnly' } },
      { name: 'SortOrder', number:   {} },
      { name: 'Source',    text:     {} },
    ],
  },
  Watch_Projects: {
    displayName: 'Watch_Projects',
    columns: [
      { name: 'ProjectId',       text:   {} },
      { name: 'Name',            text:   {} },
      { name: 'ParentProjectId', text:   {} },
      { name: 'Color',           text:   {} },
      { name: 'SortOrder',       number: {} },
      { name: 'Status',          text:   {} },   // Active | Archived
      { name: 'ProjectType',     text:   {} },   // general | renewal | policy
      { name: 'PolicyRef',       text:   {} },   // FIG_Policies item id
      { name: 'InsuredId',       text:   {} },   // FIG_Insureds item id
    ],
  },

  // One row, key/value. Title is the key ('WATCH_SWEPT_AT'), Value the payload.
  Watch_Meta: {
    displayName: 'Watch_Meta',
    columns: [
      { name: 'Value', text: { allowMultipleLines: true } },
    ],
  },

  // Spirit — family evening intercessions, one row per line of the rite.
  // Provisioned by sharepoint-mcp/scripts/provision_prayer_intentions_list.js
  // (docs/specs/SPIRIT_Intercessions_Spec_v2.md §2); this must match it
  // exactly. Title is the line itself (single-line, 255-char cap). Nothing
  // is required and nothing carries a default: an ABSENT Active reads as
  // active, so a row added from chat without the flag prays tonight, and
  // Guide is never written as '' — writers omit the key. No date columns, so
  // the list is not in STRICT_DATE_LISTS.
  Prayer_Intentions: {
    displayName: 'Prayer_Intentions',
    columns: [
      { name: 'Kind',      choice:  { choices: ['Intention', 'Leader', 'All', 'Pause'] } },
      { name: 'Guide',     text:    {} },
      { name: 'SortOrder', number:  {} },
      { name: 'Active',    boolean: {} },
    ],
  },

  // Homes — house-hunting watchlist (Role Watch) and rental comps (Role Comp).
  // Provisioned by sharepoint-mcp/scripts/provision_homes_watch_list.js; this
  // must match it exactly. Written mainly by the server's homes_* tools; the
  // app (P2) edits WatchStatus / Notes / LeaseOption. Title is the street
  // address. HomeId (HM-00001) and HarListingId are indexed + unique on the
  // list (the provisioner sets that; ensureList never creates them here).
  // Null vs zero: nothing is required, nothing has a default, unknown is
  // absent — never 0. Dates are bare 'YYYY-MM-DD', so the list is in
  // STRICT_DATE_LISTS. PriceHistory is plain multi-line JSON.
  Homes_Watch: {
    displayName: 'Homes_Watch',
    columns: [
      { name: 'HomeId',              text:     {} },
      { name: 'HarListingId',        text:     {} },
      { name: 'Role',                choice:   { choices: ['Watch', 'Comp'], allowTextEntry: false } },
      { name: 'CompOfHomeId',        text:     {} },
      { name: 'City',                text:     {} },
      { name: 'Zip',                 text:     {} },
      { name: 'Subdivision',         text:     {} },
      { name: 'HarUrl',              text:     {} },
      { name: 'RealtorUrl',          text:     {} },
      { name: 'PhotoRef',            text:     {} },
      { name: 'MlsNumber',           text:     {} },
      { name: 'LeasePrice',          number:   {} },
      { name: 'LeaseStatus',         text:     {} },
      { name: 'ForSale',             boolean:  {} },
      { name: 'SalePrice',           number:   {} },
      { name: 'Beds',                number:   {} },
      { name: 'BathsFull',           number:   {} },
      { name: 'BathsHalf',           number:   {} },
      { name: 'SqFt',                number:   {} },
      { name: 'LotSqFt',             number:   {} },
      { name: 'YearBuilt',           number:   {} },
      { name: 'RenovatedYear',       number:   {} },
      { name: 'Garage',              text:     {} },
      { name: 'ListingText',         text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'Notes',               text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'LeaseOption',         choice:   { choices: ['Unknown', 'Asked', 'Open', 'No'], allowTextEntry: false } },
      { name: 'WatchStatus',         choice:   { choices: ['Watching', 'Toured', 'Applied', 'Passed', 'Archived'], allowTextEntry: false } },
      { name: 'FoundDate',           dateTime: { format: 'dateOnly' } },
      { name: 'ListDate',            dateTime: { format: 'dateOnly' } },
      { name: 'FirstPrice',          number:   {} },
      { name: 'OriginalListPrice',   number:   {} },
      { name: 'PriceCutCount',       number:   {} },
      { name: 'LastPriceChangeDate', dateTime: { format: 'dateOnly' } },
      { name: 'PriceHistory',        text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'LeasedDate',          dateTime: { format: 'dateOnly' } },
      { name: 'FinalAskPrice',       number:   {} },
      { name: 'DaysToLease',         number:   {} },
      { name: 'LastChecked',         dateTime: { format: 'dateTime' } },
      { name: 'LastChangeNote',      text:     {} },
      { name: 'Source',              choice:   { choices: ['Manual', 'Share', 'Email'], allowTextEntry: false } },
      { name: 'LastSourceRef',       text:     {} },
      // P2 fit inputs (2026-10-04). Unscored = absent, never 0.
      { name: 'CommuteHoursWeek',    number:   {} },
      { name: 'CommuteNote',         text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'TrailScore',          number:   {} },
      { name: 'TrailNote',           text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'FamilyScore',         number:   {} },
      { name: 'FamilyNote',          text:     { allowMultipleLines: true, textType: 'plain' } },
      // P2.1 (2026-10-04). PatioScore 1–5, unscored = absent. VerifyNote:
      // semicolon-separated tour checks.
      { name: 'PatioScore',          number:   {} },
      { name: 'PatioNote',           text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'VerifyNote',          text:     { allowMultipleLines: true, textType: 'plain' } },
      // Commute v2 (2026-10-04): machine-scored by the server (homes-commute.js)
      // against Homes_Anchors. The app reads these; it does not write them.
      { name: 'Lat',                   number:   {} },
      { name: 'Lng',                   number:   {} },
      { name: 'GeocodeSource',         text:     {} },
      { name: 'CommuteSplitHoursWeek', number:   {} },
      { name: 'CommuteMilesWeek',      number:   {} },
      { name: 'RoutedMilesJson',       text:     { allowMultipleLines: true, textType: 'plain' } },
      { name: 'CommuteScoredAt',       dateTime: { format: 'dateTime' } },
    ],
  },
};

// Read-only view of a list's declared columns, for tests and callers that
// need to know what the allowlist will let through. Never the live schema.
export function getListDefinition(listName) {
  const def = LIST_DEFINITIONS[listName];
  return def ? { displayName: def.displayName, columns: def.columns.map(c => ({ ...c })) } : null;
}

// ============================================================
// COLUMN SYNC — ensures new columns are added to existing lists
// ============================================================

const syncedLists = {};

async function ensureMissingColumns(token, siteId, listId, listName) {
  if (syncedLists[listName]) return;
  syncedLists[listName] = true;
  const def = LIST_DEFINITIONS[listName];
  if (!def || !def.columns || def.columns.length === 0) return;
  try {
    const colsRes = await fetch(
      `${GRAPH}/sites/${siteId}/lists/${listId}/columns?$select=name,displayName&$top=200`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!colsRes.ok) return;
    const colsData = await colsRes.json();
    const existingNames = new Set((colsData.value || []).map(c => (c.name || c.displayName || '').toLowerCase()));
    // Externally owned schema: never create. Warn about any column the app
    // expects but the list lacks, then carry on with what is there.
    if (def.provision === false) {
      const missing = def.columns.filter(col => !existingNames.has(col.name.toLowerCase())).map(col => col.name);
      if (missing.length) {
        console.warn(`[figApi] ${listName} is missing column(s) ${missing.join(', ')} — schema is owned by its provisioning script; nothing created`);
      }
      return;
    }
    for (const col of def.columns) {
      if (!existingNames.has(col.name.toLowerCase())) {
        const colBody = { name: col.name, displayName: col.name };
        if (col.text) colBody.text = col.text;
        if (col.number) colBody.number = col.number;
        if (col.boolean) colBody.boolean = col.boolean;
        if (col.dateTime) colBody.dateTime = col.dateTime;
        if (col.choice) colBody.choice = col.choice;
        await fetch(`${GRAPH}/sites/${siteId}/lists/${listId}/columns`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(colBody),
        });
      }
    }
  } catch { /* column sync should never block */ }
}

// ============================================================
// ENSURE LIST — find or create, then sync columns
// ============================================================

async function ensureList(token, listName) {
  if (cachedListIds[listName]) return cachedListIds[listName];
  if (!listIdPromises[listName]) {
    listIdPromises[listName] = findOrCreateList(token, listName)
      .finally(() => { delete listIdPromises[listName]; });
  }
  return listIdPromises[listName];
}

async function findOrCreateList(token, listName) {
  const siteId = await resolveSiteId(token);

  // Try to get the list directly by displayName
  const directRes = await fetch(
    `${GRAPH}/sites/${siteId}/lists?$filter=displayName eq '${listName}'&$select=displayName,id`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (directRes.ok) {
    const directData = await directRes.json();
    const match = (directData.value || []).find(
      l => l.displayName.toLowerCase() === listName.toLowerCase()
    );
    if (match) {
      cachedListIds[listName] = match.id;
      await ensureMissingColumns(token, siteId, match.id, listName);
      return match.id;
    }
  }

  // Fallback: scan all lists
  const listsRes = await fetch(
    `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=200`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (listsRes.ok) {
    const listsData = await listsRes.json();
    const existing = (listsData.value || []).find(
      l => l.displayName.toLowerCase() === listName.toLowerCase()
    );
    if (existing) {
      cachedListIds[listName] = existing.id;
      await ensureMissingColumns(token, siteId, existing.id, listName);
      return existing.id;
    }
  }

  // List not found — create it
  const def = LIST_DEFINITIONS[listName];
  if (!def) throw new Error(`No FIG list definition for "${listName}"`);
  if (def.provision === false) {
    throw new Error(`List "${listName}" was not found and is not created by the app — run its provisioning script (sharepoint-mcp/scripts)`);
  }

  const createRes = await fetch(`${GRAPH}/sites/${siteId}/lists`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      displayName: def.displayName,
      list: { template: 'genericList' },
    }),
  });

  // 409 = list already exists (race condition)
  if (createRes.status === 409) {
    const retryRes = await fetch(
      `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=200`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!retryRes.ok) throw new Error(`List "${listName}" exists but failed to resolve it (${retryRes.status})`);
    const retryData = await retryRes.json();
    const found = (retryData.value || []).find(
      l => l.displayName.toLowerCase() === listName.toLowerCase()
    );
    if (found) {
      cachedListIds[listName] = found.id;
      return found.id;
    }
    throw new Error(`List "${listName}" reported as existing but could not be found`);
  }

  if (!createRes.ok) throw new Error(`Failed to create list "${listName}" (${createRes.status}): ${await createRes.text()}`);
  const newList = await createRes.json();
  const listId = newList.id;

  // Add custom columns
  for (const col of def.columns) {
    const colBody = { name: col.name, displayName: col.name };
    if (col.text) colBody.text = col.text;
    if (col.number) colBody.number = col.number;
    if (col.boolean) colBody.boolean = col.boolean;
    if (col.dateTime) colBody.dateTime = col.dateTime;
    if (col.choice) colBody.choice = col.choice;

    const colRes = await fetch(`${GRAPH}/sites/${siteId}/lists/${listId}/columns`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(colBody),
    });
    if (!colRes.ok) {
      console.warn(`Failed to create column "${col.name}" on "${listName}": ${colRes.status}`);
    }
  }

  cachedListIds[listName] = listId;
  return listId;
}

// ============================================================
// CRUD OPERATIONS
// ============================================================

function listBase(siteId, listId) {
  return `${GRAPH}/sites/${siteId}/lists/${listId}`;
}

export async function getItems(token, listName) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const headers = { Authorization: `Bearer ${token}` };

  let url = `${base}/items?$expand=fields&$top=500`;
  const allItems = [];
  while (url) {
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Failed to load ${listName} (${res.status}): ${await res.text()}`);
    const data = await res.json();
    allItems.push(...(data.value || []));
    url = data['@odata.nextLink'] || null;
  }
  return allItems.map(item => ({ id: item.id, ...item.fields }));
}

// getItems, trimmed: only the named columns, and none of the per-item wrapper
// Graph adds (createdBy, lastModifiedBy, parentReference, webUrl…) — on
// Finance_Transactions that wrapper was two thirds of a 6.5 MB read. Pages of
// 5000, following nextLink. `filter` is an OData $filter on fields/…; filter
// only on an indexed column (a list past 5000 rows refuses anything else).
// Same row shape as getItems: { id, ...fields }.
export async function getItemsSelect(token, listName, columns, { filter } = {}) {
  if (!Array.isArray(columns) || columns.length === 0) throw new Error(`getItemsSelect(${listName}): columns required`);
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const headers = { Authorization: `Bearer ${token}` };

  let url = `${base}/items?$select=id&$expand=fields($select=${columns.join(',')})&$top=5000`
    + (filter ? `&$filter=${encodeURIComponent(filter)}` : '');
  const allItems = [];
  while (url) {
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Failed to load ${listName} (${res.status}): ${await res.text()}`);
    const data = await res.json();
    allItems.push(...(data.value || []));
    url = data['@odata.nextLink'] || null;
  }
  return allItems.map(item => ({ id: item.id, ...item.fields }));
}

// Finance_Transactions is read as date windows in parallel, on its indexed
// Date column, plus a sweep for rows with no Date — together they cover every
// row exactly once:
//   Date <  b1 | b1 <= Date < b2 | b2 <= Date < b3 | Date >= b3 | Date null
// Boundaries are 6, 12 and 18 months back from `today` (first of the month),
// so the windows stay roughly even as history grows at the far end.
// `test` is the same rule in memory, for the tests.
export function transactionWindows(today = new Date()) {
  const ym = (back) => {
    const d = new Date(today.getFullYear(), today.getMonth() - back, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const [b3, b2, b1] = [ym(6), ym(12), ym(18)];
  const day = (r) => (r.Date ? String(r.Date).slice(0, 10) : null);
  return [
    { name: `< ${b1}`,          filter: `fields/Date lt '${b1}'`,                              test: (r) => day(r) !== null && day(r) < b1 },
    { name: `${b1} – ${b2}`,    filter: `fields/Date ge '${b1}' and fields/Date lt '${b2}'`,   test: (r) => day(r) !== null && day(r) >= b1 && day(r) < b2 },
    { name: `${b2} – ${b3}`,    filter: `fields/Date ge '${b2}' and fields/Date lt '${b3}'`,   test: (r) => day(r) !== null && day(r) >= b2 && day(r) < b3 },
    { name: `>= ${b3}`,         filter: `fields/Date ge '${b3}'`,                              test: (r) => day(r) !== null && day(r) >= b3 },
    { name: 'no Date',          filter: 'fields/Date eq null',                                 test: (r) => day(r) === null },
  ];
}

// One Finance list by its FINANCE_LISTS key, with its FINANCE_COLUMNS select.
// TRANSACTIONS goes through the parallel windows; a row returned by two
// windows (never, if the windows are right) is kept once.
export async function getFinanceItems(token, key, { today = new Date() } = {}) {
  const listName = FINANCE_LISTS[key];
  const columns = FINANCE_COLUMNS[key];
  if (!listName || !columns) throw new Error(`getFinanceItems: unknown list key ${key}`);
  if (key !== 'TRANSACTIONS') return getItemsSelect(token, listName, columns);
  // Resolve the site + list id once before fanning out.
  await ensureList(token, listName);
  const parts = await Promise.all(transactionWindows(today).map(w => getItemsSelect(token, listName, columns, { filter: w.filter })));
  const byId = new Map();
  for (const part of parts) for (const row of part) byId.set(row.id, row);
  return [...byId.values()];
}

// SharePoint dateOnly columns hold a full instant; 'dateOnly' is a display
// format, not a storage constraint. Two shapes can coexist in one column and
// the tenant does not normalize between them (both verified live via MCP,
// 2026-08-02):
//
//   sent '2026-08-02'           -> stored as site-local midnight,
//                                  reads back '2026-08-02T07:00:00Z'
//   sent '2026-08-03T01:00:00Z' -> stored and returned VERBATIM, untouched
//
// The site's regional timezone is UTC-7 (Pacific), not Central. That offset is
// why an instant is not obviously wrong on write: 8pm Aug 2 Central is 01:00Z
// Aug 3, which SharePoint renders site-local as 6pm Aug 2, the right day. The
// breakage is on the READ side — fromDateOnly slices the UTC string, so that
// row returns '2026-08-03', a day late, while a bare-date row slices correctly.
//
// toDateOnly and fromDateOnly are therefore a matched pair, and neither is
// sound alone: the slice is valid only because every row is guaranteed the
// bare-date shape on the way in. Callers send a bare calendar date; a Date is
// read from LOCAL components, never toISOString(), which emits precisely the
// instant shape this guard exists to exclude.
//
// This guard lives in figApi only. Graph accepts a non-conforming instant on
// this column without error, so MCP writes and provisioning scripts bypass it
// and can leave rows the slice will misread.
//
// Enforcement is opt-in per list. FamilyVault_Media_Items.DateOfEvent is the
// other dateOnly column and it is fed unvalidated free text from
// ClaimModal.js:284 (an f() field with no type argument, so type="text").
// Enforcing there would turn tolerated-sloppy input into a runtime throw on a
// live user path. Fix ClaimModal to type="date" first, then add it here.
const STRICT_DATE_LISTS = new Set(['Food_Log', 'Watch_Open', 'Watch_Sources', 'Homes_Watch', 'FIG_LossRunRequests', 'FIG_LossRunResolutions', 'FIG_Insureds']);
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function toDateOnly(value, colName = 'date', listName = 'list') {
  if (value === '' || value === null || value === undefined) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new Error(`${listName}.${colName}: received an Invalid Date`);
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof value === 'string') {
    if (DATE_ONLY_RE.test(value)) return value;
    throw new Error(
      `${listName}.${colName}: expected 'YYYY-MM-DD' or a Date, got '${value}'. ` +
      `Never send toISOString() output to a dateOnly column — it shifts evening ` +
      `entries to the next day. Pass toDateOnly(dateObject) instead.`
    );
  }
  throw new Error(`${listName}.${colName}: unsupported dateOnly value (${typeof value})`);
}

// Read side, for Phase 4. Graph returns dateOnly as a full ISO instant; slice
// rather than parse. Correct ONLY for rows written in the bare-date shape — see
// the block above. A row carrying an arbitrary instant slices to the wrong day.
export function fromDateOnly(value) {
  return value ? String(value).slice(0, 10) : null;
}

// Food_Log.Time is a TEXT column, not a dateTime one, and that is the whole
// point. A dateTime carries an instant, and an instant carries a timezone: the
// same column would then have to answer "18:15 where?" — a question the reading
// does not have an answer to. Date on this list is already dateOnly for exactly
// that reason (see the block above), and pairing a timezone-free date with a
// timezone-bearing time would reintroduce, in the adjacent column, the failure
// the dateOnly guard exists to prevent.
//
// The contract is therefore a string: LOCAL wall clock, 24-hour, zero-padded
// 'HH:MM'. Local to whoever logged the entry, which is the only reading of a
// meal time that is ever wanted. No offset, no seconds, no AM/PM. Zero-padded
// 24-hour is also lexically sortable, so the read path orders the day's ledger
// with a string compare and never parses a clock at all.
//
// Same philosophy as toDateOnly: THROW, never coerce. A time this guard cannot
// vouch for is a bug at the call site, and quietly rewriting it to something
// plausible would bury that bug in the data.
//
// And the same limitation: this guard lives in figApi only. The column is plain
// text with a 255-char max, so Graph accepts 'lunchtime' or '5:00 PM' or an
// empty string without complaint. MCP writes and provisioning scripts bypass
// this exactly as they bypass the date guard, and can leave rows that conform
// to nothing. fromTimeOnly is the matched read-side half: it refuses to hand
// such a value to the UI dressed as a valid time.
const STRICT_TIME_LISTS = new Set(['Food_Log']);
const TIME_ONLY_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Which columns are times, per list. A dateOnly column announces itself by
// shape (col.dateTime?.format === 'dateOnly'); a time column cannot, because it
// is text and so are FoodId, Unit and Notes. There is nothing to detect, so the
// columns are named outright — anything not named here is passed through as the
// ordinary text it is.
const TIME_COLUMNS = {
  Food_Log: new Set(['Time']),
};

function isTimeCol(listName, colName) {
  return STRICT_TIME_LISTS.has(listName) && (TIME_COLUMNS[listName]?.has(colName) ?? false);
}

export function toTimeOnly(value, colName = 'Time', listName = 'list') {
  if (value === '' || value === null || value === undefined) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new Error(`${listName}.${colName}: received an Invalid Date`);
    }
    // LOCAL components, never getUTCHours(). A 6pm dinner read in UTC is 23:15
    // or 01:15 depending on the season, and either one is the wrong dinner.
    const h = String(value.getHours()).padStart(2, '0');
    const m = String(value.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
  if (typeof value === 'string') {
    if (TIME_ONLY_RE.test(value)) return value;
    throw new Error(
      `${listName}.${colName}: expected 24-hour 'HH:MM' or a Date, got '${value}'. ` +
      `This column is local wall clock as text — no AM/PM, no seconds, no offset, ` +
      `no toISOString(). Pass toTimeOnly(dateObject) instead.`
    );
  }
  throw new Error(`${listName}.${colName}: unsupported time value (${typeof value})`);
}

// Read side, and the reason the write guard can be trusted only halfway. A value
// that does not conform is returned as null rather than as itself: a row written
// through an unguarded door is missing a usable time, and the view must be able
// to say so. Rendering '5:00 PM' or 'lunchtime' verbatim would present
// unvalidated text as though this guard had approved it.
export function fromTimeOnly(value) {
  return typeof value === 'string' && TIME_ONLY_RE.test(value) ? value : null;
}

export async function createItem(token, listName, fields) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);

  // Sanitize: allowlist + coerce empty-string numbers to null (same as updateItem)
  const def = LIST_DEFINITIONS[listName];
  const allowedCols = new Set(['Title']);
  const numberCols = new Set();
  const dateOnlyCols = new Set();
  const timeCols = new Set();
  if (def) {
    for (const col of def.columns) {
      allowedCols.add(col.name);
      if (col.number) numberCols.add(col.name);
      if (STRICT_DATE_LISTS.has(listName) && col.dateTime?.format === 'dateOnly') {
        dateOnlyCols.add(col.name);
      }
      if (isTimeCol(listName, col.name)) timeCols.add(col.name);
    }
  }
  const sanitized = {};
  for (const [key, value] of Object.entries(fields)) {
    if (!allowedCols.has(key)) continue;
    if (dateOnlyCols.has(key)) {
      sanitized[key] = toDateOnly(value, key, listName);
      continue;
    }
    if (timeCols.has(key)) {
      sanitized[key] = toTimeOnly(value, key, listName);
      continue;
    }
    sanitized[key] = (numberCols.has(key) && value === '') ? null : value;
  }

  const res = await fetch(`${base}/items`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fields: sanitized }),
  });
  if (!res.ok) throw new Error(`Failed to create item in ${listName} (${res.status}): ${await res.text()}`);
  const created = await res.json();
  console.log(`[createItem] ${listName} id=${created.id} status=${res.status}`);
  return { id: created.id, ...created.fields };
}

// opts.keepalive: true lets the PATCH outlive the page (a flush from
// beforeunload); the body must stay under 64 KB, which a fields PATCH does.
export async function updateItem(token, listName, itemId, fields, opts = {}) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);

  // Allowlist: only send fields defined in LIST_DEFINITIONS columns + Title
  // This automatically excludes all SharePoint system/read-only fields
  const def = LIST_DEFINITIONS[listName];
  const allowedCols = new Set(['Title']);
  const numberCols = new Set();
  // Multi-select choice columns (rendered as checkBoxes) are MultiChoice in
  // SharePoint. Graph rejects a write to one unless the payload carries a
  // "<Column>@odata.type": "Collection(Edm.String)" annotation alongside the
  // array, so it is added here rather than left to every caller.
  const multiChoiceCols = new Set();
  const dateOnlyCols = new Set();
  const timeCols = new Set();
  if (def) {
    for (const col of def.columns) {
      allowedCols.add(col.name);
      if (col.number) numberCols.add(col.name);
      if (col.choice?.displayAs === 'checkBoxes') multiChoiceCols.add(col.name);
      if (STRICT_DATE_LISTS.has(listName) && col.dateTime?.format === 'dateOnly') {
        dateOnlyCols.add(col.name);
      }
      if (isTimeCol(listName, col.name)) timeCols.add(col.name);
    }
  }
  const sanitized = {};
  for (const [key, value] of Object.entries(fields)) {
    if (!allowedCols.has(key)) continue;
    if (multiChoiceCols.has(key)) {
      sanitized[`${key}@odata.type`] = 'Collection(Edm.String)';
      sanitized[key] = Array.isArray(value) ? value : (value ? [value] : []);
      continue;
    }
    if (dateOnlyCols.has(key)) {
      sanitized[key] = toDateOnly(value, key, listName);
      continue;
    }
    if (timeCols.has(key)) {
      sanitized[key] = toTimeOnly(value, key, listName);
      continue;
    }
    // Empty strings on number columns must be null for SharePoint
    sanitized[key] = (numberCols.has(key) && value === '') ? null : value;
  }

  const res = await fetch(`${base}/items/${itemId}/fields`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sanitized),
    ...(opts.keepalive === true ? { keepalive: true } : {}),
  });
  if (!res.ok) throw new Error(`Failed to update item in ${listName} (${res.status}): ${await res.text()}`);
  console.log(`[updateItem] ${listName} id=${itemId} status=${res.status} payload=`, sanitized);
  const updated = await res.json();
  return { id: itemId, ...updated };
}

// opts.keepalive: true lets the DELETE outlive the page (a flush from
// pagehide / beforeunload of a held delete), same as updateItem.
export async function deleteItem(token, listName, itemId, opts = {}) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const res = await fetch(`${base}/items/${itemId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    ...(opts.keepalive === true ? { keepalive: true } : {}),
  });
  if (!res.ok) throw new Error(`Failed to delete item in ${listName} (${res.status}): ${await res.text()}`);
}

// ============================================================
// FIG LIST NAMES — export for convenience
// ============================================================

export const FIG_LISTS = {
  INSUREDS: 'FIG_Insureds',
  BUILDINGS: 'FIG_Buildings',
  OFFICERS: 'FIG_Officers',
  VEHICLES: 'FIG_Vehicles',
  POLICIES: 'FIG_Policies',
  AGENCY: 'FIG_Agency',
  CONTACTS: 'FIG_Contacts',
  CARRIER_HISTORY: 'FIG_CarrierHistory',
  LOSS_HISTORY: 'FIG_LossHistory',
  CLASS_CODES: 'FIG_ClassCodes',
  COVERAGES: 'FIG_Coverages',
  GENERAL_QUESTIONS: 'FIG_GeneralQuestions',
  WC_RATING: 'FIG_WCRating',
  ADDITIONAL_INTERESTS: 'FIG_AdditionalInterests',
  LAYER_PARTICIPANTS: 'FIG_LayerParticipants',
  ENDORSEMENTS: 'FIG_Endorsements',
  CARRIERS: 'FIG_Carriers',
  POLICY_FEES: 'FIG_PolicyFees',
  INVOICES: 'FIG_Invoices',
  SETTINGS: 'FIG_Settings',
  TRIAGE_QUEUE: 'TriageQueue',
  LINES_OF_BUSINESS: 'FIG_LinesOfBusiness',
  LOSS_RUN_SOURCES: 'FIG_LossRunSources',
  LOSS_RUN_REQUESTS: 'FIG_LossRunRequests',
  LOSS_RUN_RESOLUTIONS: 'FIG_LossRunResolutions',
};

export const FAMILY_LISTS = {
  MEDIA_ITEMS: 'FamilyVault_Media_Items',
  BOOKMARKS:   'FamilyVault_Bookmarks',
  USER_PROGRESS: 'FamilyVault_UserProgress',
  NAMED_BOOKMARKS: 'FamilyVault_NamedBookmarks',
};

// Every column the Finance page and its Net Worth / Recurring tabs read, per
// list — the ONE place the select lists live. getFinanceItems asks Graph for
// these and nothing else. A column the code reads but this list leaves out
// comes back absent, which the page cannot tell from blank (Graph omits blank
// fields too) — so src/finance/__tests__/financeColumns.test.js records every
// field the page reads and fails if one is missing here.
export const FINANCE_COLUMNS = {
  TRANSACTIONS: ['Date', 'Amount', 'Direction', 'Context', 'CategoryLookupId', 'Title', 'Notes', 'IsPending', 'SourceStatement', 'AccountLookupId', 'MerchantName', 'ReceiptRef'],
  CATEGORIES:   ['Title', 'Group'],
  ACCOUNTS:     ['Mask'],
  BALANCES:     ['Date', 'Context', 'Mask', 'Title', 'Current', 'Available', 'Modified'],
  ASSETS:          ['Title', 'Notes', 'AssetType', 'Value', 'Quantity', 'Unit', 'Updated'],
  HOLDINGS:        ['Date', 'AccountLookupId', 'Value', 'IsCash', 'CostBasis', 'Ticker', 'Title', 'Quantity', 'Price', 'PriceAsOf'],
  INVESTMENT_TXNS: ['Date', 'Type', 'AccountLookupId', 'Amount', 'Ticker'],
  RECURRING:       ['Status', 'Context', 'MatchType', 'MatchValue', 'AccountMask', 'Title', 'Cadence', 'ExpectedAmount', 'AmountTolerancePct', 'StartDate', 'EndDate', 'Scheduled', 'PaidThrough', 'DayOfMonth', 'Category', 'IsEstimate'],
};

export const FINANCE_LISTS = {
  TRANSACTIONS: 'Finance_Transactions',
  CATEGORIES:   'Finance_Categories',
  ACCOUNTS:     'Finance_Accounts',
  BALANCES:     'Finance_Balances',
  // Net Worth tab. Read-only from the page: these stay OUT of LIST_DEFINITIONS,
  // so ensureList can find them but never create a list or add a column.
  ASSETS:          'Finance_Assets',
  HOLDINGS:        'Finance_Holdings',
  INVESTMENT_TXNS: 'Finance_InvestmentTxns',
  // Recurring tab + forward Runway. Read-only from the page, same as above.
  RECURRING:       'Finance_Recurring',
};

export const FOOD_LISTS = {
  LOG: 'Food_Log',
};

export const HOMES_LISTS = {
  WATCH: 'Homes_Watch',
};

export const NOTES_LISTS = {
  NOTE_LINKS: 'FIG_NoteLinks',
};

// Ensure all FIG lists exist (call once on module init)
export async function ensureAllFIGLists(token) {
  const results = {};
  for (const [key, listName] of Object.entries(FIG_LISTS)) {
    try {
      results[key] = await ensureList(token, listName);
    } catch (err) {
      console.warn(`[FIG] Failed to ensure list ${listName}:`, err.message);
      results[key] = null;
    }
  }
  return results;
}

export async function ensureAllFamilyLists(token) {
  const results = {};
  for (const [key, listName] of Object.entries(FAMILY_LISTS)) {
    try {
      results[key] = await ensureList(token, listName);
    } catch (err) {
      console.warn(`[Family] Failed to ensure list ${listName}:`, err.message);
      results[key] = null;
    }
  }
  return results;
}

// ============================================================
// FIG VAULT — SharePoint Document Library for file storage
// ============================================================

const FIG_VAULT_NAME = 'FIGVault';
let cachedVaultDriveId = null;

async function resolveVaultDriveId(token) {
  if (cachedVaultDriveId) return cachedVaultDriveId;
  const siteId = await resolveSiteId(token);
  // Try to find existing library by name
  const res = await fetch(
    `${GRAPH}/sites/${siteId}/drives?$select=name,id&$top=100`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (res.ok) {
    const data = await res.json();
    const match = (data.value || []).find(
      d => d.name.toLowerCase() === FIG_VAULT_NAME.toLowerCase()
    );
    if (match) {
      cachedVaultDriveId = match.id;
      return cachedVaultDriveId;
    }
  }
  return null;
}

export async function ensureFIGVault(token) {
  // Check if already exists
  const existing = await resolveVaultDriveId(token);
  if (existing) return existing;

  // Create the document library
  const siteId = await resolveSiteId(token);
  const createRes = await fetch(`${GRAPH}/sites/${siteId}/lists`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      displayName: FIG_VAULT_NAME,
      list: { template: 'documentLibrary' },
    }),
  });

  if (createRes.status === 409) {
    // Already exists (race condition) — resolve it
    cachedVaultDriveId = null;
    const driveId = await resolveVaultDriveId(token);
    if (driveId) return driveId;
    throw new Error(`${FIG_VAULT_NAME} reported as existing but could not resolve its drive ID`);
  }

  if (!createRes.ok) {
    throw new Error(`Failed to create ${FIG_VAULT_NAME} (${createRes.status}): ${await createRes.text()}`);
  }

  // Resolve the drive ID for the newly created library
  cachedVaultDriveId = null;
  const driveId = await resolveVaultDriveId(token);
  if (!driveId) throw new Error(`Created ${FIG_VAULT_NAME} but could not resolve its drive ID`);
  return driveId;
}

export async function createFIGVaultFolder(token, folderPath) {
  const driveId = await ensureFIGVault(token);
  // Graph API: create folder via PUT with folder facet
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${folderPath}:/children`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: folderPath.split('/').pop(),
        folder: {},
        '@microsoft.graph.conflictBehavior': 'fail',
      }),
    }
  );
  // 409 = folder already exists, that's fine
  if (res.status === 409) return;
  if (!res.ok) {
    throw new Error(`Failed to create folder "${folderPath}" in ${FIG_VAULT_NAME} (${res.status}): ${await res.text()}`);
  }
  return res.json();
}

export async function uploadToFIGVault(token, filePath, fileContent) {
  const driveId = await ensureFIGVault(token);
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${filePath}:/content`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/octet-stream',
      },
      body: fileContent,
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to upload "${filePath}" to ${FIG_VAULT_NAME} (${res.status}): ${await res.text()}`);
  }
  return res.json();
}

// Cache carrier list in memory — it's large (2700+) and rarely changes
// SharePoint Excel import assigned generic field names: field_2=CompanyName, field_12=CarrierType, etc.
let cachedCarriers = null;

export async function searchCarriers(token, searchText) {
  if (!cachedCarriers) {
    console.log('[searchCarriers] Loading full carrier list...');
    const allItems = [];
    const siteId = await resolveSiteId(token);
    const listId = await ensureList(token, 'FIG_Carriers');
    const base = listBase(siteId, listId);

    let url = `${base}/items?$expand=fields&$top=500`;
    while (url) {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load carriers (${res.status})`);
      const data = await res.json();
      // Map SharePoint generic field names to our schema
      allItems.push(...(data.value || []).map(item => {
        const f = item.fields || {};
        return {
          id: item.id,
          NAICCode: f.Title || f.NAICCode || '',
          NAICGroupCode: f.field_1 || f.NAICGroup || '',
          CompanyName: f.field_2 || f.CompanyName || '',
          FEIN: f.field_3 || f.FEIN || '',
          Address: f.field_4 || f.Address || '',
          City: f.field_5 || f.City || '',
          State: f.field_6 || f.State || '',
          Zip: f.field_7 || f.Zip || '',
          Phone: f.field_8 || f.Phone || '',
          DomicileState: f.field_9 || f.DomicileState || '',
          CompanyType: f.field_10 || f.CompanyType || '',
          LicensedLines: f.field_11 || f.LicensedLines || '',
          CarrierType: f.field_12 || f.CarrierType || '',
          IsMyCarrier: f.IsMyCarrier || false,
        };
      }));
      url = data['@odata.nextLink'] || null;
    }
    cachedCarriers = allItems.filter(c => c.CompanyName);
    console.log('[searchCarriers] Cached', cachedCarriers.length, 'carriers');
  }

  const query = searchText.toLowerCase();

  const matches = cachedCarriers.filter(c =>
    c.CompanyName.toLowerCase().includes(query)
  );
  // Sort: IsMyCarrier first, then alphabetically
  matches.sort((a, b) => {
    if (a.IsMyCarrier && !b.IsMyCarrier) return -1;
    if (!a.IsMyCarrier && b.IsMyCarrier) return 1;
    return a.CompanyName.localeCompare(b.CompanyName);
  });
  return matches.slice(0, 20);
}

// ============================================================
// NOTES — FIG_NoteLinks CRUD
// ============================================================

export async function getNoteLinks(token, { sourceItemType, sourceItemId, userUpn }) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, 'FIG_NoteLinks');
  const base   = listBase(siteId, listId);
  const filter = encodeURIComponent(
    `fields/SourceItemType eq '${sourceItemType}' and fields/SourceItemId eq '${sourceItemId}' and fields/UserUpn eq '${userUpn}'`
  );
  const res = await fetch(`${base}/items?$expand=fields&$filter=${filter}&$top=500`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: 'HonorNonIndexedQueriesWarningMayFailRandomly',
    },
  });
  if (!res.ok) throw new Error(`Failed to load note links (${res.status}): ${await res.text()}`);
  const data = await res.json();
  return (data.value || []).map(item => ({
    id:               item.id,
    title:            item.fields?.Title            || '',
    sourceItemType:   item.fields?.SourceItemType   || '',
    sourceItemId:     item.fields?.SourceItemId     || '',
    oneNotePageId:    item.fields?.OneNotePageId    || '',
    oneNoteClientUrl: item.fields?.OneNoteClientUrl || '',
    oneNoteWebUrl:    item.fields?.OneNoteWebUrl    || '',
    userUpn:          item.fields?.UserUpn          || '',
    createdAt:        item.fields?.CreatedAt        || item.createdDateTime || '',
  }));
}

export async function createNoteLink(token, {
  title, sourceItemType, sourceItemId,
  oneNotePageId, oneNoteClientUrl, oneNoteWebUrl, userUpn,
}) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, 'FIG_NoteLinks');
  const base   = listBase(siteId, listId);
  const res = await fetch(`${base}/items`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fields: {
        Title:            title,
        SourceItemType:   sourceItemType,
        SourceItemId:     sourceItemId,
        OneNotePageId:    oneNotePageId,
        OneNoteClientUrl: oneNoteClientUrl,
        OneNoteWebUrl:    oneNoteWebUrl,
        UserUpn:          userUpn,
        CreatedAt:        new Date().toISOString(),
      },
    }),
  });
  if (!res.ok) throw new Error(`Failed to create note link (${res.status}): ${await res.text()}`);
  return res.json();
}

export async function deleteNoteLink(token, linkId) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, 'FIG_NoteLinks');
  const base   = listBase(siteId, listId);
  const res = await fetch(`${base}/items/${linkId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to delete note link (${res.status}): ${await res.text()}`);
}

export async function deleteNoteLinksByPageId(token, pageId) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, 'FIG_NoteLinks');
  const base   = listBase(siteId, listId);
  const filter = encodeURIComponent(`fields/OneNotePageId eq '${pageId}'`);
  const res = await fetch(`${base}/items?$expand=fields&$filter=${filter}&$top=500`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: 'HonorNonIndexedQueriesWarningMayFailRandomly',
    },
  });
  if (!res.ok) throw new Error(`Failed to query note links for page ${pageId} (${res.status}): ${await res.text()}`);
  const data = await res.json();
  for (const item of (data.value || [])) {
    await deleteNoteLink(token, item.id);
  }
}

export async function getFromFIGVault(token, filePath) {
  const driveId = await ensureFIGVault(token);
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${filePath}:/content`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to download "${filePath}" from ${FIG_VAULT_NAME} (${res.status}): ${await res.text()}`);
  }
  return res.blob();
}

// ============================================================
// MARKET CONDITIONS — Ivans Index rate data
// ============================================================

const MARKET_CONDITIONS_LIST_ID = '32842f9c-8e21-4815-9e85-8e43c92a7dc2';

export async function getMarketConditions(token) {
  const siteId = await resolveSiteId(token);
  const base = listBase(siteId, MARKET_CONDITIONS_LIST_ID);
  const res = await fetch(`${base}/items?$expand=fields($select=Title,LOB,RateChange,PriorMonth,Direction,Source,PublishedDate,MarketType,DistReductionFlat,Dist1to9,Dist10to19,Dist20Plus)&$top=500`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to load FIG_MarketConditions (${res.status}): ${await res.text()}`);
  const data = await res.json();
  return (data.value || []).map(item => ({ id: item.id, ...item.fields }));
}
